/* eslint-disable react/function-component-definition */

import React from 'react';

import getConfig from 'next/config';
import Image from 'next/image';

import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import { Button, IconButton } from '@mui/material';

import SwipeableViews from 'react-swipeable-views';
import { bindKeyboard } from 'react-swipeable-views-utils';

import fallbackThumbnail from '@kamernet/assets/video-fallback-thumbnail.webp';
import { ONE_PIXEL_BLURRED_PLACEHOLDER_IMAGE } from '@kamernet/constants/Common';
import { useIntl } from '@kamernet/core/Intl';
import { getImageUrl } from '@kamernet/utilities/Resources/getImageUrl';

import { FlipBaseVideoPlayer } from './FlipBaseVideoPlayer';
import styles from './LightBox.module.css';

const { publicRuntimeConfig } = getConfig();

export const EnhancedSwipeableViews = bindKeyboard(SwipeableViews);

const getThumbnails = (videoKeys: string[], thumbnailImageKeys: string[]) => {
  const fallbackVideoThumbnails = videoKeys.map(() => '');
  return [...fallbackVideoThumbnails, ...thumbnailImageKeys];
};

export interface LightBoxProps {
  shareButtonSlot?: React.ReactNode;
  videoKeys?: string[] | null;
  imageKeys?: string[] | null;
  thumbnailImageKeys?: string[] | null;
  imgAltPrefix: string;
  onClose: () => void;
}

export const LightBox = ({
  shareButtonSlot = undefined,
  videoKeys = [],
  imageKeys = [],
  thumbnailImageKeys = [],
  imgAltPrefix,
  onClose,
}: LightBoxProps) => {
  const { T } = useIntl();

  const thumbnailKeys = getThumbnails(
    videoKeys || [],
    thumbnailImageKeys || [],
  );

  const [currentIndex, setCurrentIndex] = React.useState(0);
  const totalCount = (imageKeys?.length || 0) + (videoKeys?.length || 0);

  const goToNext = React.useCallback(() => {
    setCurrentIndex(prevIndex => (prevIndex + 1) % totalCount);
  }, [totalCount]);

  const goToPrevious = React.useCallback(() => {
    setCurrentIndex(prevIndex => (prevIndex - 1 + totalCount) % totalCount);
  }, [totalCount]);

  return (
    <div className={styles.root}>
      <IconButton
        size="small"
        className={styles.navigationButtonDesktop}
        onClick={goToPrevious}
      >
        <ArrowBackIosIcon fontSize="small" />
      </IconButton>

      <div className={styles.lightbox}>
        <div className={styles.header}>
          <div>
            <IconButton className={styles.closeMobile}>
              <CloseIcon className={styles.headerIcons} onClick={onClose} />
            </IconButton>
            <Button
              className={styles.closeDesktop}
              startIcon={<CloseIcon />}
              onClick={onClose}
            >
              {T('LBL_CLOSE')}
            </Button>
          </div>
          <span>{`${currentIndex + 1} / ${totalCount}`}</span>
          {shareButtonSlot}
        </div>
        {/* Don't remove this redundant code for EnhancedSwipeableViews.
            This was introduced to fix bug in bindKeyBoard library which
            creates an EMPTY div for the conditional rendering while
            navigating with keyboard. */}
        {videoKeys && videoKeys.length > 0 ? (
          <EnhancedSwipeableViews
            className={styles.imageContainer}
            index={currentIndex}
            containerStyle={{
              width: '100%',
              height: '100%',
              position: 'relative',
            }}
            slideStyle={{
              overflow: 'visible',
              height: '100%',
              transition: 'opacity 0.5s ease-in-out',
            }}
            onChangeIndex={setCurrentIndex}
          >
            {videoKeys.map(videoKey => {
              return (
                <FlipBaseVideoPlayer
                  key={videoKey}
                  videoId={videoKey}
                  playerId={
                    publicRuntimeConfig.FLIPBASE_VIDEO_PLAYER_ID ||
                    '1fe8ca3a-558d-4640-afbe-87575cf3191c'
                  }
                />
              );
            })}
            {(imageKeys || []).map((imageKey, index) => (
              <div key={imageKey} className={styles.imageWrapper}>
                <Image
                  layout="fill"
                  objectFit="contain"
                  src={getImageUrl(imageKey)}
                  alt={`${imgAltPrefix} - ${index + 1}`}
                />
              </div>
            ))}
          </EnhancedSwipeableViews>
        ) : (
          <EnhancedSwipeableViews
            className={styles.imageContainer}
            index={currentIndex}
            containerStyle={{
              width: '100%',
              height: '100%',
              position: 'relative',
            }}
            slideStyle={{
              overflow: 'visible',
              height: '100%',
              transition: 'opacity 0.5s ease-in-out',
            }}
            onChangeIndex={setCurrentIndex}
          >
            {(imageKeys || []).map((imageKey, index) => (
              <div key={imageKey} className={styles.imageWrapper}>
                <Image
                  layout="fill"
                  objectFit="contain"
                  src={getImageUrl(imageKey)}
                  alt={`${imgAltPrefix} - ${index + 1}`}
                />
              </div>
            ))}
          </EnhancedSwipeableViews>
        )}

        <div className={styles.mobileNavButtons}>
          <IconButton
            size="small"
            className={styles.navigationButtonMobile}
            onClick={goToPrevious}
          >
            <ArrowBackIosIcon />
          </IconButton>
          <IconButton
            size="small"
            className={styles.navigationButtonMobile}
            onClick={goToNext}
          >
            <ArrowForwardIosIcon />
          </IconButton>
        </div>

        <div className={styles.thumbnails}>
          {thumbnailKeys.map((thumbnailKey, index) => (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
            <Image
              key={thumbnailKey}
              loading="lazy"
              width={128}
              height={96}
              src={
                thumbnailKey ? getImageUrl(thumbnailKey) : fallbackThumbnail.src
              }
              alt={`${imgAltPrefix} ${index + 1}`}
              className={
                currentIndex === index
                  ? styles.activeThumbnail
                  : styles.thumbnail
              }
              placeholder="blur"
              blurDataURL={ONE_PIXEL_BLURRED_PLACEHOLDER_IMAGE}
              onClick={() => setCurrentIndex(index)}
            />
          ))}
        </div>
      </div>

      <IconButton
        size="small"
        className={styles.navigationButtonDesktop}
        onClick={goToNext}
      >
        <ArrowForwardIosIcon />
      </IconButton>
    </div>
  );
};
